<template>
    <resizable-columns :storage-key="resource" :active="resizeActive">
        <div
            :class="{
                active: isListActive,
                'has-footer': !!$slots['list-footer'],
                'list-dominant': isListDominant,
                'list-panel--table': tableMode,
                'list-panel--stepped-aside': listHidden
            }"
            class="list-panel top-nav"
            :style="panelStyle"
        >
            <!-- Normal navbar -->
            <nav
                v-if="checked.length === 0"
                ref="toolbarRef"
                class="navbar has-shadow"
                :class="{ 'is-compact': compactToolbar }"
            >
                <div class="navbar-menu">
                    <div class="navbar-start" :class="{ 'has-filter-bar': showFilterBar }">
                        <slot
                            name="filters"
                            :filters="filters"
                            :total="total"
                            :refreshing="refreshing"
                            :lastLoadedAt="lastLoadedAt"
                        >
                            <!-- Default: the title, with an optional count badge -->
                            <div v-if="title" class="navbar-item list-filter-trigger">
                                <span>{{ title }}</span>
                                <span
                                    v-if="showCount"
                                    class="tag is-rounded is-light"
                                    :class="{ 'is-invisible': total === null }"
                                >
                                    {{ total }}
                                </span>
                            </div>
                        </slot>

                        <!-- The list's most asked filters, one chip each, wherever the
                             table shows: as the toolbar narrows they step aside behind
                             « +N » (measureToolbar). The narrow rail keeps the
                             « Filtrer » menu alone, which holds them too. -->
                        <div v-if="showFilterBar" class="navbar-item baselist-filter-bar">
                            <list-filter-bar
                                :filters="orderedFilters"
                                :fields="filterBar"
                                :hidden="hiddenChips"
                                @more="openFilterMenu"
                            />
                        </div>
                    </div>
                    <div class="navbar-end">
                        <!-- Freshness indicator: shows when the list last synced and
                             spins while a background refresh runs. Clicking forces one. -->
                        <a
                            v-if="refreshOnFocus"
                            class="navbar-item baselist-toolbar-btn baselist-freshness"
                            @click="forceRefresh"
                        >
                            <b-tooltip :label="freshnessLabel" position="is-bottom" :delay="300" append-to-body>
                                <b-icon icon="sync" size="is-small" :class="{ 'is-spinning': refreshing }"></b-icon>
                            </b-tooltip>
                        </a>

                        <!-- Built-in extra filters dropdown -->
                        <smart-dropdown
                            v-if="extraFilters?.length || $slots['extra-filters'] || orderedFilters.length"
                            ref="filterMenuRef"
                            preferred-position="is-bottom-left"
                        >
                            <template #trigger>
                                <a
                                    class="navbar-item baselist-toolbar-btn"
                                    :class="{ 'has-text-primary': hasActiveExtraFilters }"
                                    :title="$t('Filter by')"
                                >
                                    <b-icon size="is-small" icon="filter-variant"></b-icon>
                                    <span v-if="tableMode" class="is-hidden-touch baselist-toolbar-label">
                                        {{ $t('Filter') }}
                                    </span>
                                </a>
                            </template>
                            <template v-for="(group, gi) in extraFilters" :key="group.key">
                                <hr v-if="gi > 0" class="dropdown-divider" />
                                <b-dropdown-item custom>
                                    <strong>{{ group.label }}</strong>
                                </b-dropdown-item>
                                <b-dropdown-item
                                    v-for="opt in group.options"
                                    :key="group.key + '-' + opt.value"
                                    has-link
                                >
                                    <router-link
                                        :to="getExtraFilterRoute(group.key, opt.value)"
                                        class="dropdown-item baselist-filter-option"
                                    >
                                        <b-icon
                                            icon="check"
                                            size="is-small"
                                            :class="{ 'is-invisible': !isExtraFilterActive(group.key, opt.value) }"
                                        ></b-icon>
                                        {{ opt.label }}
                                    </router-link>
                                </b-dropdown-item>
                            </template>
                            <!-- The filters the module declares, in the order the org
                                 gave its columns — its own, one per text column and one
                                 per custom field. -->
                            <hr v-if="orderedFilters.length && extraFilters?.length" class="dropdown-divider" />
                            <list-filter-menu :filters="orderedFilters" />

                            <hr v-if="$slots['extra-filters']" class="dropdown-divider" />
                            <slot name="extra-filters" :filters="filters"></slot>
                            <template v-if="hasActiveExtraFilters">
                                <hr class="dropdown-divider" />
                                <b-dropdown-item has-link>
                                    <router-link :to="clearExtraFiltersRoute" class="dropdown-item has-text-danger">
                                        <b-icon icon="close" size="is-small"></b-icon>
                                        {{ $t('Clear filters') }}
                                    </router-link>
                                </b-dropdown-item>
                            </template>
                        </smart-dropdown>

                        <!-- Built-in display dropdown: sorting and grouping. Two settings of
                             the same shape — a list of choices with a check mark — held by one
                             control, so the narrow rail keeps a button for the page actions
                             ("…") without spilling past its 275px. Section headings appear
                             only when both halves exist; a list that only sorts keeps exactly
                             the menu it had. -->
                        <smart-dropdown
                            v-if="effectiveSortOptions.length || groupByOptions?.length"
                            preferred-position="is-bottom-left"
                        >
                            <template #trigger>
                                <a
                                    class="navbar-item baselist-toolbar-btn"
                                    :class="{ 'has-text-primary': activeGroupBy }"
                                    :title="displayMenuTitle"
                                >
                                    <b-icon size="is-small" icon="sort"></b-icon>
                                    <span v-if="tableMode" class="is-hidden-touch baselist-toolbar-label">
                                        {{ displayMenuLabel }}
                                    </span>
                                </a>
                            </template>

                            <template v-if="effectiveSortOptions.length">
                                <b-dropdown-item v-if="hasSortAndGroup" custom class="baselist-menu-heading">
                                    {{ $t('Sort by') }}
                                </b-dropdown-item>
                                <b-dropdown-item v-for="s in effectiveSortOptions" :key="s.field" has-link>
                                    <router-link
                                        :to="getSortRoute(s.field)"
                                        class="dropdown-item baselist-filter-option"
                                    >
                                        {{ s.label }}
                                        <b-icon
                                            :icon="getSortIcon(s.field)"
                                            size="is-small"
                                            :class="{ 'is-invisible': !isSortActive(s.field) }"
                                        ></b-icon>
                                    </router-link>
                                </b-dropdown-item>
                            </template>

                            <template v-if="groupByOptions?.length">
                                <hr v-if="hasSortAndGroup" class="dropdown-divider" />
                                <b-dropdown-item v-if="hasSortAndGroup" custom class="baselist-menu-heading">
                                    {{ $t('Group by') }}
                                </b-dropdown-item>
                                <b-dropdown-item has-link>
                                    <router-link :to="getGroupByRoute('')" class="dropdown-item baselist-filter-option">
                                        <b-icon
                                            icon="check"
                                            size="is-small"
                                            :class="{ 'is-invisible': activeGroupBy }"
                                        ></b-icon>
                                        {{ $t('No grouping') }}
                                    </router-link>
                                </b-dropdown-item>
                                <b-dropdown-item v-for="g in groupByOptions" :key="g.field" has-link>
                                    <router-link
                                        :to="getGroupByRoute(g.field)"
                                        class="dropdown-item baselist-filter-option"
                                    >
                                        <b-icon
                                            icon="check"
                                            size="is-small"
                                            :class="{ 'is-invisible': activeGroupBy !== g.field }"
                                        ></b-icon>
                                        {{ g.label }}
                                    </router-link>
                                </b-dropdown-item>
                            </template>
                        </smart-dropdown>

                        <slot name="actions" :filters="filters"></slot>

                        <!-- Built-in overflow menu ("…"), on every list: page items via
                             the #menu slot, then the way back to the list as it first
                             opens, "reset column widths" for resizable grids that have
                             no column-manager panel (no objectType — catalog grids host
                             the width reset in the panel instead), and the settings. -->
                        <smart-dropdown preferred-position="is-bottom-left">
                            <template #trigger>
                                <a class="navbar-item baselist-toolbar-btn" :title="$t('More')">
                                    <b-icon size="is-small" icon="dots-vertical"></b-icon>
                                </a>
                            </template>

                            <slot name="menu" :filters="filters"></slot>
                            <hr v-if="$slots.menu" class="dropdown-divider" />

                            <b-dropdown-item has-link>
                                <a class="dropdown-item baselist-filter-option" @click="resetView">
                                    <b-icon icon="restore" size="is-small"></b-icon>
                                    {{ $t('Reset view') }}
                                </a>
                            </b-dropdown-item>

                            <template v-if="tableMode && !objectType">
                                <b-dropdown-item has-link>
                                    <a class="dropdown-item baselist-filter-option" @click="resetColumnWidths">
                                        <b-icon icon="arrow-collapse-horizontal" size="is-small"></b-icon>
                                        {{ $t('Reset column widths') }}
                                    </a>
                                </b-dropdown-item>
                            </template>

                            <!-- The column manager, last. The header gear only exists in the
                                 full table; this keeps it reachable from the narrow list too. -->
                            <template v-if="objectType">
                                <b-dropdown-item has-link>
                                    <a class="dropdown-item baselist-filter-option" @click="columnPanelOpen = true">
                                        <b-icon icon="cog" size="is-small"></b-icon>
                                        {{ $t('Settings') }}
                                    </a>
                                </b-dropdown-item>
                            </template>
                        </smart-dropdown>

                        <template v-if="showAdd">
                            <!-- Full-width table on desktop: prominent primary button -->
                            <div v-if="tableMode" class="navbar-item baselist-add-item is-hidden-touch">
                                <b-button
                                    type="is-primary"
                                    size="is-small"
                                    icon-left="plus"
                                    :title="$t('Add')"
                                    @click="handleAdd()"
                                >
                                    <span>{{ $t('Add') }}</span>
                                </b-button>
                            </div>
                            <!-- Narrow list, or any touch device: compact icon button -->
                            <a
                                @click="handleAdd()"
                                :title="$t('Add')"
                                class="navbar-item"
                                :class="{ 'is-hidden-desktop': tableMode }"
                            >
                                <b-tooltip :label="$t('Add')" :delay="500" append-to-body>
                                    <b-icon icon="plus" size="is-small"></b-icon>
                                </b-tooltip>
                            </a>
                        </template>
                    </div>
                </div>
            </nav>

            <!-- What the list is not showing: a line the module fills from what the
                 endpoint reports about the result set (see useList's `meta`). -->
            <div v-if="$slots.notice" ref="noticeRef" class="list-notice">
                <slot name="notice" :meta="meta" :filters="filters"></slot>
            </div>

            <!-- Selection navbar. It sits *under* the notice, directly on top of
                 the rows: the bar acts on what is ticked below it, and on a list
                 carrying a header block (the time sheet's period and chart) a bar
                 above that block reads as belonging to the block. -->
            <nav v-if="checked.length > 0" class="navbar has-shadow">
                <div class="navbar-menu">
                    <div class="navbar-start">
                        <div class="navbar-item">
                            <b-checkbox
                                :model-value="checkedAll"
                                @update:model-value="toggleAll"
                                :title="$t('Select all')"
                            >
                                <!-- How many rows the bar is about to act on, next to the
                                     label — the same tag the list header uses for its total.
                                     Where the label has no room (touch, panel mode) the
                                     count says it in full instead. -->
                                <span v-if="tableMode" class="is-hidden-touch">
                                    {{ $t('Select all') }}
                                    <span class="tag is-rounded is-light" :title="selectedLabel">
                                        {{ checked.length }}
                                    </span>
                                </span>
                                <span :class="{ 'is-hidden-desktop': tableMode }">{{ selectedLabel }}</span>
                            </b-checkbox>
                        </div>
                    </div>
                    <div class="navbar-end">
                        <!-- The same shape on every list: the pencil holds every field the
                             selection can be updated with (`selection-edit`), then whatever
                             the list can *do* with a selection (`selection-actions`), then
                             the overflow — destructive last. A module fills them; where they
                             sit is decided here, so the bar reads the same everywhere.

                             An action lives in the bar rather than under the "…" when it is
                             the reason people select rows in the first place: nobody opens an
                             overflow menu to find out what a selection is good for. -->
                        <smart-dropdown v-if="$slots['selection-edit']" preferred-position="is-bottom-left">
                            <template #trigger>
                                <a class="navbar-item baselist-toolbar-btn" :title="$t('Bulk update')">
                                    <b-icon icon="pencil" size="is-small"></b-icon>
                                    <span v-if="tableMode" class="is-hidden-touch">{{ $t('Bulk update') }}</span>
                                </a>
                            </template>

                            <slot name="selection-edit"></slot>
                        </smart-dropdown>

                        <slot name="selection-actions" :checked="checked" :tableMode="tableMode"></slot>

                        <smart-dropdown preferred-position="is-bottom-left">
                            <template #trigger>
                                <a class="navbar-item baselist-toolbar-btn" :title="$t('More')">
                                    <b-icon size="is-small" icon="dots-vertical"></b-icon>
                                </a>
                            </template>

                            <slot name="selection-menu"></slot>

                            <hr v-if="$slots['selection-menu']" class="dropdown-divider" />
                            <b-dropdown-item has-link>
                                <a class="dropdown-item" @click="clearSelection">
                                    <b-icon icon="checkbox-blank-off-outline" size="is-small"></b-icon>
                                    {{ $t('Clear selection') }}
                                </a>
                            </b-dropdown-item>
                            <b-dropdown-item has-link>
                                <a class="dropdown-item has-text-danger" @click="handleRemoveSelection">
                                    <b-icon icon="delete" size="is-small"></b-icon>
                                    {{ $t('Remove selected') }}
                                </a>
                            </b-dropdown-item>
                        </smart-dropdown>
                    </div>
                </div>
            </nav>

            <!-- Full-width table (shown when no item is selected and columns are configured) -->
            <div v-if="tableMode" ref="tableListRef" v-drag-scroll="'.table-wrapper'" class="content-list list-table">
                <b-table
                    ref="tableRef"
                    :key="tableColumnsKey"
                    v-resizable-cols="resizeColsConfig"
                    :data="displayItems"
                    :default-sort="[currentSortField, currentSortOrder]"
                    :row-class="(row, index) => (row._isGroupHeader ? 'is-group-header-row' : rowClass(row, index))"
                    :mobile-cards="false"
                    backend-sorting
                    hoverable
                    @sort="onTableSort"
                    @click="openRow"
                >
                    <b-table-column v-if="checkable" width="40">
                        <!-- Column manager trigger lives in the (otherwise empty)
                             checkbox header, so it stays visible at the far left even
                             when the grid scrolls horizontally. -->
                        <template v-if="objectType" #header>
                            <a
                                class="grid-col-manager"
                                :title="$t('Manage columns')"
                                @click.stop="columnPanelOpen = true"
                            >
                                <b-icon icon="cog" size="is-small" />
                            </a>
                        </template>
                        <template #default="{ row }">
                            <span v-if="!row._isGroupHeader" @click.stop>
                                <b-checkbox :native-value="row.id" v-model="checked" />
                            </span>
                        </template>
                    </b-table-column>

                    <b-table-column
                        v-for="(col, colIndex) in tableColumns"
                        :key="col.field"
                        :label="col.label"
                        :field="col.field"
                        :sortable="col.sortable"
                        :width="col.width"
                        :numeric="col.numeric"
                        v-slot="{ row }"
                    >
                        <span v-if="row._isGroupHeader && colIndex === 0" class="table-group-header">
                            <span class="has-text-weight-bold has-text-grey">{{ row.label }}</span>
                            <span class="tag is-light is-rounded ml-1">{{ row.count }}</span>
                        </span>
                        <slot v-else-if="!row._isGroupHeader" :name="`col-${col.field}`" :row="row">
                            {{ getColValue(row, col.field) }}
                        </slot>
                    </b-table-column>

                    <template #footer>
                        <infinite-loading
                            v-if="!activeGroupBy"
                            ref="infiniteLoading"
                            @infinite="onInfiniteLoad"
                            :identifier="infiniteLoadingId"
                        >
                            <!-- A list that already explains its own failure on
                                 screen replaces the generic red line, which would
                                 otherwise contradict the explanation. -->
                            <template v-if="$slots['list-error']" #error>
                                <slot name="list-error"></slot>
                            </template>
                            <template #complete>
                                <app-empty
                                    v-if="rows.length === 0"
                                    :label="emptyLabel"
                                    :action-label="emptyActionLabel"
                                    @action="addFromSearchText"
                                />
                            </template>
                        </infinite-loading>
                        <app-empty
                            v-else-if="lastPage && rows.length === 0"
                            :label="emptyLabel"
                            :action-label="emptyActionLabel"
                            @action="addFromSearchText"
                        />
                    </template>
                </b-table>
            </div>

            <!-- List -->
            <slot
                v-else
                name="list-content"
                :rows="displayItems"
                :onInfiniteLoad="onInfiniteLoad"
                :infiniteLoadingId="infiniteLoadingId"
            >
                <div class="content-list menu" tabindex="0" @keydown="onKeydown">
                    <ul ref="menuListRef" :class="['menu-list', { checkable: props.checkable }]">
                        <template v-for="item in displayItems" :key="item.id">
                            <li v-if="item._isGroupHeader" class="group-header-item">
                                <div class="group-header">
                                    <span class="has-text-weight-bold has-text-grey">{{ item.label }}</span>
                                    <span class="tag is-light is-rounded ml-1">{{ item.count }}</span>
                                </div>
                            </li>
                            <li v-else>
                                <b-checkbox v-if="props.checkable" :native-value="item.id" v-model="checked" />
                                <slot name="item" :row="item" :isActive="isRowActive(item.id)">
                                    <!-- A record with no page of its own is not a
                                         link: there is nowhere to open in a new
                                         tab, and a link that opens a modal lies
                                         about where it goes. -->
                                    <component
                                        :is="previewRoute ? 'router-link' : 'a'"
                                        v-bind="previewRoute ? { to: getItemRoute(item) } : {}"
                                        :class="['list-item-link', { 'is-active': isRowActive(item.id) }]"
                                        @click="previewRoute ? null : openRow(item)"
                                    >
                                        <slot name="item-before" :row="item"></slot>
                                        <div class="list-item-content">
                                            <div class="list-item-label">{{ getItemLabel(item) }}</div>
                                            <slot name="item-details" :row="item"></slot>
                                        </div>
                                        <slot name="item-after" :row="item"></slot>
                                    </component>
                                </slot>
                            </li>
                        </template>

                        <li>
                            <infinite-loading
                                v-if="!activeGroupBy"
                                ref="infiniteLoading"
                                @infinite="onInfiniteLoad"
                                :identifier="infiniteLoadingId"
                            >
                                <!-- See the table-mode sibling above: a list that
                                     explains its own failure replaces this line. -->
                                <template v-if="$slots['list-error']" #error>
                                    <slot name="list-error"></slot>
                                </template>
                                <template #complete>
                                    <app-empty
                                        v-if="rows.length === 0"
                                        :label="emptyLabel"
                                        :action-label="emptyActionLabel"
                                        @action="addFromSearchText"
                                    />
                                </template>
                            </infinite-loading>
                            <app-empty
                                v-else-if="lastPage && rows.length === 0"
                                :label="emptyLabel"
                                :action-label="emptyActionLabel"
                                @action="addFromSearchText"
                            />
                        </li>
                    </ul>
                </div>
            </slot>

            <!-- Footer pinned at the bottom of the list panel (mobile only; desktop uses the table-mode footer) -->
            <div v-if="$slots['list-footer']" class="list-footer">
                <slot name="list-footer"></slot>
            </div>

            <!-- Pinned footer (e.g. quick-add), below the rows. Tied to the
                 width and to the list being the active panel, not to the
                 full-width table: a list whose columns are not configured, or
                 not yet loaded, is still a list somebody adds a row to — and
                 gating it on the table is what made the bar come and go.
                 Below the table width the `list-footer` above does the job, so
                 the two never both appear. -->
            <div v-if="isTableWidth && isListActive && $slots['content-footer']" class="list-table-footer">
                <slot name="content-footer"></slot>
            </div>
        </div>

        <!-- Right region (only when a detail panel is shown; hidden in full-width table mode).
             A preview owns its own pinned footer/composer, so the list no longer renders one here. -->
        <template v-if="!tableMode">
            <slot name="helper" v-if="isListActive">
                <app-helper></app-helper>
            </slot>

            <router-view v-slot="{ Component }">
                <component v-if="Component" :is="Component" :style="previewFlexStyle" />
            </router-view>
        </template>

        <!-- Per-user column manager (show/hide + reorder + reset widths), opened from the header gear -->
        <column-manager-panel
            v-if="objectType"
            :object-type="objectType"
            :resource="resource"
            v-model:open="columnPanelOpen"
        />
    </resizable-columns>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick, onMounted, onBeforeUnmount, provide, useSlots } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useApi } from '@/services/api';
import { useList } from '@/composables/useList';
import { useOrgStore } from '@/stores/org';
import { useGridPreferencesStore } from '@/stores/gridPreferences';
import ColumnManagerPanel from '@/components/base/ColumnManagerPanel.vue';
import { useKeyboardNav } from '@/composables/useKeyboardNav';
import { useQualifiers } from '@/composables/useQualifiers';
import { useActiveList } from '@/composables/useActiveList';
import ListFilterMenu from '@/components/base/ListFilterMenu.vue';
import ListFilterBar from '@/components/base/ListFilterBar.vue';
import { queryAfterReset } from '@/utils/listReset';
import { useNow } from '@/composables/useNow';

const props = defineProps({
    // API resource name (e.g., 'contacts', 'products')
    resource: {
        type: String,
        required: true
    },
    // Form component for add/edit
    formComponent: {
        type: Object,
        default: null
    },
    // Route name for item preview
    // Where a row opens. Leave it empty for records that have no page of their
    // own — a time entry is four fields, not a screen — and clicking a row opens
    // its form instead of navigating.
    previewRoute: {
        type: String,
        default: ''
    },
    // Route name for list (for redirects after delete)
    listRoute: {
        type: String,
        required: true
    },
    // Page title
    title: {
        type: String,
        default: ''
    },
    // Field to display as item label
    labelField: {
        type: String,
        default: 'display_name'
    },
    // Optional function to compute item label (overrides labelField)
    labelFn: {
        type: Function,
        default: null
    },
    // Show add button
    showAdd: {
        type: Boolean,
        default: true
    },
    // Adding navigates to this route instead of opening the form modal.
    // A route name, or a full route location object.
    addRoute: {
        type: [String, Object],
        default: null
    },
    // Where an add lands, when the record's preview is not the whole answer —
    // a contract raised for e-signature opens straight in its designer, because
    // placing the fields is the rest of creating it. Receives the created row,
    // returns a route location; anything falsy keeps the default (the preview,
    // carrying the list's query). This exists so a module never has to re-implement
    // the Add button just to change where it goes.
    afterAdd: {
        type: Function,
        default: null
    },
    // What happens before the add form opens — a screen asking *how* to start
    // (a blank project, or one of the organization's templates). Returns, or
    // resolves with, extra form data; rejecting cancels the add. The same
    // reason as `afterAdd`: a module changes what Add does without
    // re-implementing the button.
    beforeAdd: {
        type: Function,
        default: null
    },
    // Default data to pass to form when adding a new item
    formData: {
        type: Object,
        default: () => ({})
    },
    // What a search that found nothing becomes. Receives the text typed in the
    // search bar, returns the fields the create form should open with — or
    // nothing, for a list where the searched text says nothing about the record
    // (an invoice is not created out of the number someone looked for). Declaring
    // it is what puts the "Add « … »" offer in the empty list.
    addFromSearch: {
        type: Function,
        default: null
    },
    // Filter fields to sync with route query (e.g., ['query', 'type'])
    filterFields: {
        type: Array,
        default: () => ['query']
    },
    // Sort options: [{ field: 'name', label: 'Name' }]
    sortOptions: {
        type: Array,
        default: () => []
    },
    // Default sort when no query param is set: { field: 'name', order: 'asc' }
    defaultSort: {
        type: Object,
        default: null
    },
    // Extra filter groups: [{ label: 'Priority', key: 'priority', options: [{ value: '', label: 'All' }, ...] }]
    extraFilters: {
        type: Array,
        default: () => []
    },
    // Declared filters standing in the toolbar as chips, between the title and the
    // « Filtrer » button — desktop table only. Keys of the list's qualifiers, or
    // { key, label?, toggle?, icon? }: see ListFilterBar.vue.
    filterBar: {
        type: Array,
        default: () => []
    },
    // Group-by options: [{ field: 'status', label: 'Status' }]
    groupByOptions: {
        type: Array,
        default: () => []
    },
    // Function to extract group key/label: (row, field) => { key, label }
    groupKeyFn: {
        type: Function,
        default: null
    },
    /**
     * The groups that exist whether or not a row falls in them:
     * (field) => [{ key, label }]. A phase with nothing in it is still a
     * phase, and a heading is where its first row gets added.
     */
    groupSeedFn: {
        type: Function,
        default: null
    },
    // Show checkboxes for bulk selection
    checkable: {
        type: Boolean,
        default: true
    },
    // Flex-grow for the left (list) column
    listFlex: {
        type: [Number, String],
        default: null
    },
    // Flex-grow for the right (preview) column
    previewFlex: {
        type: [Number, String],
        default: null
    },
    // (row) => RouteLocation — where a row opens, when it is not the resource's
    // own preview route. Used with `preview-route` so the list still knows a
    // row has somewhere to go.
    itemRoute: {
        type: Function,
        default: null
    },
    // This list lives inside another record's screen (a preview tab), so the
    // id in the route belongs to its host and never means a row of this list
    // is selected.
    embedded: {
        type: Boolean,
        default: false
    },
    // The query the list opens on when the page sets one itself (Tasks: my open
    // tasks). « Reset view » comes back to it rather than to an unfiltered list.
    // An object, or a function returning one.
    defaultQuery: {
        type: [Object, Function],
        default: null
    },
    // Static params merged into every API call (not synced with URL)
    staticParams: {
        type: Object,
        default: () => ({})
    },
    // Full-width table columns shown when no item is selected.
    // Each column: { field, label, sortable?, width?, numeric? }.
    // Custom cell content via the scoped slot `#col-<field>="{ row }"`.
    // When empty, the list keeps the narrow single-column layout.
    tableColumns: {
        type: Array,
        default: () => []
    },
    // ListColumnCatalog object type for this grid (e.g. 'contacts', 'tasks').
    // When set, the full-width table shows a column manager (gear in the checkbox
    // header) letting each user show/hide and reorder columns for themselves.
    objectType: {
        type: String,
        default: ''
    },
    // Row class function for the full-width table: (row, index) => string
    rowClass: {
        type: Function,
        default: () => ''
    },
    // Request the authoritative total row count (matching the active filters)
    // and expose it to the `filters` slot as `total`. Off by default so lists
    // that never display a count don't pay for the extra COUNT query.
    showCount: {
        type: Boolean,
        default: false
    },
    // Keep the list fresh in the background: silently re-fetch when the tab
    // regains focus with stale data, and poll while it stays visible. Opt-in,
    // so lists that don't need it pay nothing. Also renders the freshness
    // indicator in the toolbar.
    refreshOnFocus: {
        type: Boolean,
        default: false
    },
    // Age (ms) beyond which regaining focus triggers a refresh. Short
    // alt-tabs stay free.
    staleAfter: {
        type: Number,
        default: 60000
    },
    // Background poll while the tab is visible (ms). 0 disables polling and
    // leaves only the focus-driven refresh.
    pollInterval: {
        type: Number,
        default: 120000
    }
});

const emit = defineEmits(['added', 'edited', 'removed', 'reset', 'load-error']);

const route = useRoute();
const router = useRouter();
const slots = useSlots();
const orgStore = useOrgStore();
const gridPreferences = useGridPreferencesStore();

// Template ref for menu list
const menuListRef = ref(null);

// The filters this list declares (module qualifiers + one per custom field the
// org added). Keyed by the object type, which is also the search descriptor key.
const { qualifiers: listQualifiers, load: loadListQualifiers } = useQualifiers();
const { setActiveList, clearActiveList } = useActiveList();

watch(
    () => props.objectType,
    objectType => {
        if (objectType) {
            loadListQualifiers(objectType);
        }
    },
    { immediate: true }
);

// Effective filter fields = explicit filterFields + auto-detected from sort/extraFilters
const effectiveFilterFields = computed(() => {
    const fields = [...props.filterFields];
    // Sorting always travels through the route query (sort_field/sort_order) —
    // whether the sort menu is passed via sortOptions or auto-derived from the
    // sortable columns. Forward them unconditionally so a sorted URL or a header
    // click actually reaches the API; getQueryParams only sends them when set.
    // (Gating this on sortOptions.length silently dropped the sort on any list
    // that relies on the column-derived menu, e.g. Contacts.)
    if (!fields.includes('sort_field')) fields.push('sort_field');
    if (!fields.includes('sort_order')) fields.push('sort_order');
    // The advanced query travels as one parameter of its own, next to the flat
    // filters rather than instead of them (see App\Search\Query\QueryTree).
    if (!fields.includes('q')) fields.push('q');
    if (props.extraFilters?.length) {
        props.extraFilters.forEach(group => {
            if (!fields.includes(group.key)) fields.push(group.key);
        });
    }
    // Every filter the module declares — including the one generated for each of
    // the org's custom fields — travels through the URL like the hand-listed
    // ones. Without this a filter is offered by the search bar, lands in the
    // URL, and is dropped on the way to the API.
    listQualifiers.value.forEach(qualifier => {
        if (!fields.includes(qualifier.key)) fields.push(qualifier.key);
    });

    return fields;
});

/**
 * The filters offered by the "Filter by" menu.
 *
 * The order is the descriptor's: the backend already returns the list's fields in
 * the organization's column order, then the filters that match no column, then
 * the custom fields. Sorting again here is how the menu and the search bar ended
 * up disagreeing. Drill-down parameters stay out.
 */
const orderedFilters = computed(() => listQualifiers.value.filter(qualifier => !qualifier.hidden));

// The filter bar needs the room of the full table and the list's declared filters.
const showFilterBar = computed(() => tableMode.value && props.filterBar.length > 0 && orderedFilters.value.length > 0);

// Filter state - reactive object with keys from filterFields
const filters = reactive({});

// The declared filters land after the descriptor answers, so their keys join the
// state then. Adopt them quietly, and reload only when the URL already carries
// one — otherwise every list would refetch once on mount for nothing.
watch(
    () => effectiveFilterFields.value,
    fields => {
        let carriesNewFilter = false;

        fields.forEach(field => {
            if (filters[field] === undefined) {
                filters[field] = route.query[field] || '';
                carriesNewFilter = carriesNewFilter || !!filters[field];
            }
        });

        if (carriesNewFilter) {
            handleReset();
        }
    }
);

// Initialize filter fields
function initFilters() {
    effectiveFilterFields.value.forEach(field => {
        filters[field] = '';
    });
}
initFilters();

// Get query params for API calls from filters + static params
function getQueryParams() {
    const params = { ...props.staticParams };
    effectiveFilterFields.value.forEach(field => {
        if (filters[field]) {
            params[field] = filters[field];
        }
    });
    return params;
}

// Determine if list should reset based on route query change
function shouldResetOnQueryChange(newQuery) {
    for (const field of effectiveFilterFields.value) {
        const newVal = newQuery[field] || '';
        if (filters[field] !== newVal) {
            return true;
        }
    }
    return false;
}

// Sync filter state from route query
function syncFiltersFromRoute() {
    effectiveFilterFields.value.forEach(field => {
        filters[field] = route.query[field] || '';
    });
}

// --- Sort & Extra filter helpers ---

function isSortActive(field) {
    const currentField = route.query.sort_field || props.defaultSort?.field || '';
    return currentField === field;
}

function getSortIcon(field) {
    if (!isSortActive(field)) return 'chevron-up';
    const currentOrder = route.query.sort_order || props.defaultSort?.order || 'asc';
    return currentOrder === 'asc' ? 'chevron-up' : 'chevron-down';
}

function getSortRoute(field) {
    const currentField = route.query.sort_field || props.defaultSort?.field || '';
    const currentOrder = route.query.sort_order || props.defaultSort?.order || 'asc';
    const newOrder = currentField === field ? (currentOrder === 'asc' ? 'desc' : 'asc') : currentOrder;
    return { name: route.name, query: { ...route.query, sort_field: field, sort_order: newOrder } };
}

function isExtraFilterActive(key, value) {
    const current = route.query[key] || '';
    return String(current) === String(value);
}

function getExtraFilterRoute(key, value) {
    if (!value && value !== 0) {
        const q = { ...route.query };
        delete q[key];
        return { name: route.name, query: q };
    }
    return { name: route.name, query: { ...route.query, [key]: value } };
}

// The "Sort" dropdown must mirror the grid: every sortable column shown in the
// full-width table becomes a sort option, in the same order. Any explicit
// `sortOptions` the page passes are appended (deduped by field) for sorts not
// tied to a visible column. This is the house standard — the sort menu and the
// sortable headers can never drift, and a catalog-driven list needs no
// hand-maintained sort list.
const effectiveSortOptions = computed(() => {
    const fromColumns = (props.tableColumns || [])
        .filter(column => column.sortable)
        .map(column => ({ field: column.field, label: column.label }));

    const seen = new Set(fromColumns.map(option => option.field));
    const extras = (props.sortOptions || []).filter(option => !seen.has(option.field));

    return [...fromColumns, ...extras];
});

const hasActiveExtraFilters = computed(() => {
    const excluded = new Set(['query', 'sort_field', 'sort_order', 'group_by']);
    return effectiveFilterFields.value.some(field => !excluded.has(field) && !!route.query[field]);
});

const clearExtraFiltersRoute = computed(() => {
    const excluded = new Set(['query', 'sort_field', 'sort_order', 'group_by']);
    const q = { ...route.query };
    effectiveFilterFields.value.forEach(field => {
        if (!excluded.has(field)) delete q[field];
    });
    return { name: route.name, query: q };
});

// --- Group-by helpers ---

const activeGroupBy = computed(() => route.query.group_by || '');

// Sorting and grouping share one button. When a list offers both, the control is
// named for what it does as a whole and each half gets a heading; when it offers
// only one, it keeps that one's own wording so nothing reads renamed.
const hasSortAndGroup = computed(() => !!effectiveSortOptions.value.length && !!props.groupByOptions?.length);

const displayMenuTitle = computed(() => {
    if (hasSortAndGroup.value) return i18n.t('Sort and group');
    return effectiveSortOptions.value.length ? i18n.t('Sort by') : i18n.t('Group by');
});

const displayMenuLabel = computed(() => {
    if (hasSortAndGroup.value) return i18n.t('Display');
    return effectiveSortOptions.value.length ? i18n.t('Sort') : i18n.t('Group');
});

function getGroupByRoute(field) {
    if (!field) {
        const q = { ...route.query };
        delete q.group_by;
        return { name: route.name, query: q };
    }
    return { name: route.name, query: { ...route.query, group_by: field } };
}

const displayItems = computed(() => {
    if (!activeGroupBy.value || !props.groupByOptions?.length) {
        return rows.value;
    }
    const field = activeGroupBy.value;
    const groups = new Map();

    // Groups the screen declares up front, before any row is read: a phase
    // holding nothing is still a phase, and a grouping that only shows what
    // the rows happen to mention hides exactly the empty one somebody is
    // looking for. They come first, in the order given; anything the rows turn
    // up that was not declared is appended after.
    for (const seed of props.groupSeedFn?.(field) ?? []) {
        groups.set(String(seed.key ?? ''), { label: seed.label ?? '', rows: [] });
    }

    for (const row of rows.value) {
        let key, label;
        if (props.groupKeyFn) {
            const result = props.groupKeyFn(row, field);
            key = String(result.key ?? '');
            label = result.label ?? (key || 'None');
        } else {
            key = String(row[field] ?? '');
            label = key || 'None';
        }
        if (!groups.has(key)) {
            groups.set(key, { label, rows: [] });
        }
        groups.get(key).rows.push(row);
    }
    const items = [];
    for (const [key, group] of groups) {
        items.push({ _isGroupHeader: true, id: 'group-' + key, label: group.label, count: group.rows.length });
        items.push(...group.rows);
    }
    return items;
});

// API service (org-scoped when inside an organization, global otherwise)
const api = useApi(props.resource, route.params.org ? { orgId: () => route.params.org } : {});

// Use list composable
const {
    rows,
    checked,
    checkedAll,
    lastPage,
    total,
    meta,
    infiniteLoadingId,
    lastLoadedAt,
    refreshing,
    resetList,
    refreshList,
    toggleAll,
    addItem,
    editItem,
    removeItem,
    removeSelection,
    onInfiniteLoad: baseOnInfiniteLoad,
    loadAllPages,
    provideParentCallbacks
} = useList({
    api,
    formComponent: props.formComponent,
    displayField: props.labelField,
    route,
    router,
    getQueryParams,
    withCount: props.showCount,
    onLoadError: error => emit('load-error', error)
});

// Provide callbacks for child components
provideParentCallbacks();

// Override parentRemove to auto-redirect to list route
provide('parentRemove', (row, options = {}) => {
    return removeItem(row, { redirectRoute: props.listRoute, ...options });
});

// Keyboard navigation
const { onKeydown } = useKeyboardNav({
    checked,
    removeSelection: handleRemoveSelection,
    listRef: menuListRef
});

// Computed
/**
 * Whether this list is showing itself rather than one of its records.
 *
 * Read from the route, because a top-level list puts the selected record's id
 * there. A list **embedded** in another record's screen — a project's tasks
 * tab — cannot use that test: the id in the route is its host's, not a row of
 * its own, so the list would believe a record was selected for ever and stay
 * in the narrow layout the full-width table was configured to replace.
 */
const isListActive = computed(() => props.embedded || (!route.params.id && !route.name?.endsWith('-new')));

// How many rows the selection bar is acting on, spelled out.
const selectedLabel = computed(() => i18n.t('{count} selected', { count: checked.value.length }));

// Track the table breakpoint ($list-table in variables.scss). From there up the
// list panel spans the whole screen, so it shows the table: the narrow
// single-column row was drawn for a 275px column beside a preview, and stretched
// across a tablet it is one line of text in a sea of empty space. Only a screen
// with room for a single column — a phone held upright — keeps that row.
const TABLE_BREAKPOINT = 500;
const tableMq = window.matchMedia(`(min-width: ${TABLE_BREAKPOINT}px)`);
const isTableWidth = ref(tableMq.matches);
function onTableMqChange(e) {
    isTableWidth.value = e.matches;
}
tableMq.addEventListener('change', onTableMqChange);
onBeforeUnmount(() => tableMq.removeEventListener('change', onTableMqChange));

/**
 * Which of the three layouts this list uses.
 *
 * The organization sets the house answer (Settings ▸ Lists); a user may answer
 * differently for one list, from its column manager — a project needs the whole
 * screen in a way a contact does not, and that is a per-list judgement. Keyed by
 * the object type, so Contacts and Customers — one resource, two screens — are
 * each configured on their own.
 */
const layoutMode = computed(
    () => (props.objectType && gridPreferences.getLayoutMode(props.objectType)) || orgStore.listLayoutMode
);

// Full-width table mode: tablet width and up, no item selected and columns
// configured. The list then fills the whole width as a multi-column table;
// selecting a row (or shrinking to a phone) collapses back to the narrow list.
// `narrow` forces the list back to the classic narrow layout; `adaptive` and
// `full` both show the table.
const tableMode = computed(
    () => layoutMode.value !== 'narrow' && isTableWidth.value && isListActive.value && props.tableColumns.length > 0
);

/**
 * Where the reader was in the full-width table when a record was opened.
 *
 * Opening a record swaps the table for the narrow list, and closing it builds a
 * new table: the rows are still loaded, but a fresh table starts at the top, so
 * someone who opened a contact far down the list came back to its first line —
 * which reads as the whole list reloading. The position is taken while the old
 * table is still in the DOM (a `pre` watcher runs before the patch) and handed to
 * the new one once it is drawn.
 *
 * Tied to the rows it was measured on: a filter or sort changed while the record
 * was open resets the list (a new `infiniteLoadingId`), and that list starts at
 * its top.
 */
const tableListRef = ref(null);
let savedTableScroll = null;

watch(tableMode, (isTable, wasTable) => {
    if (wasTable && !isTable) {
        const wrapper = tableListRef.value?.querySelector('.table-wrapper');
        savedTableScroll = wrapper
            ? { top: wrapper.scrollTop, left: wrapper.scrollLeft, listId: infiniteLoadingId.value }
            : null;

        return;
    }

    if (isTable && !wasTable && savedTableScroll) {
        const saved = savedTableScroll;
        savedTableScroll = null;
        if (saved.listId !== infiniteLoadingId.value) {
            return;
        }
        nextTick(() => {
            const wrapper = tableListRef.value?.querySelector('.table-wrapper');
            if (wrapper) {
                wrapper.scrollTop = saved.top;
                wrapper.scrollLeft = saved.left;
            }
        });
    }
});

/**
 * Whether the list steps aside entirely while a record is open.
 *
 * The `full` layout means full width at every moment: the record gets the screen
 * instead of sharing it with a 275px rail, which is what a project — its phases,
 * its tasks, its money — has no room for otherwise. Leaving the list in place but
 * hidden keeps its rows and its scroll position for the way back.
 *
 * An embedded list is never hidden: it is a tab of the record on screen, not the
 * list the record was opened from.
 */
const listHidden = computed(
    () =>
        layoutMode.value === 'full' &&
        isTableWidth.value &&
        !props.embedded &&
        !isListActive.value &&
        props.tableColumns.length > 0
);

// Stored column widths should only apply when a real detail panel is shown
// beside the list: either an item is selected, or a pinned content-footer keeps
// the right region mounted. Otherwise the list fills the space (no narrow +
// empty gap) — and a hidden list is no column at all.
const resizeActive = computed(
    () => !tableMode.value && !listHidden.value && (!!slots['content-footer'] || !isListActive.value)
);

// Config for the per-user column-resize directive on the full-width grid.
// `fields` is aligned to the rendered header cells: a null placeholder for the
// leading checkbox column (kept fixed, not draggable), then one entry per data
// column. `defaultWidths` seeds each column from its configured width until the
// user drags it. Persisted per user under the grid's resource key.
const resizeColsConfig = computed(() => {
    const fields = props.checkable ? [null] : [];
    const defaultWidths = {};
    props.tableColumns.forEach(col => {
        fields.push(col.field);
        if (col.width != null) {
            defaultWidths[col.field] = col.width;
        }
    });
    return { gridKey: props.resource, fields, defaultWidths };
});

// Clear this user's saved column widths for the grid; the resize directive
// watches the store and re-applies the default layout.
function resetColumnWidths() {
    gridPreferences.resetColumnWidths(props.resource);
}

const resettingView = ref(false);

/**
 * « Reset view »: the list as it opens for someone who never touched it.
 *
 * Search, filters, advanced query, sort, grouping and the open saved view all
 * live in the route query, so they go together; the list lands on its own
 * opening query (`defaultQuery`), not on an unfiltered one. Columns, widths and
 * layout go back to the organization's configuration. Saved views are kept —
 * they are what someone deliberately set aside.
 *
 * The display is the only part a click does not bring back, so that is when the
 * reader is asked first. An embedded list shares its host's URL: only its own
 * parameters are removed, never the host's tab or back markers.
 */
async function resetView() {
    if (resettingView.value) {
        return;
    }
    resettingView.value = true;

    try {
        const displayKeys = [props.objectType, props.resource];

        if (gridPreferences.hasPersonalDisplay(displayKeys)) {
            try {
                await Modal.confirm(
                    i18n.t('Reset view'),
                    i18n.t(
                        "Search, filters and sorting are cleared, and your columns, their widths and the layout go back to the organization's configuration. Your saved views are kept."
                    )
                );
            } catch {
                return;
            }
            gridPreferences.resetListDisplay(displayKeys);
        }

        const query = queryAfterReset({
            current: route.query,
            owned: effectiveFilterFields.value,
            embedded: props.embedded,
            opening: props.defaultQuery
        });

        await router.push({ name: route.name, params: route.params, query }).catch(() => {});
    } finally {
        resettingView.value = false;
    }
}

// Column manager panel (show/hide + reorder), opened from the header gear.
const columnPanelOpen = ref(false);

// Buefy's <b-table> registers columns in mount order and won't reorder or
// reposition a re-shown column on its own. Re-key the table on the visible
// column set/order so it rebuilds cleanly when the user manages columns.
const tableColumnsKey = computed(() => props.tableColumns.map(col => col.field).join('|'));

// Current sort (route query wins, else the configured default)
const currentSortField = computed(() => route.query.sort_field || props.defaultSort?.field || '');
const currentSortOrder = computed(() => route.query.sort_order || props.defaultSort?.order || 'asc');

// Read a possibly-nested field (e.g. 'assignee.first_name') for default cell rendering
function getColValue(row, field) {
    return field.split('.').reduce((val, key) => (val == null ? val : val[key]), row);
}

// Table header sort → drive it through the route query (same channel as the sort dropdown)
function onTableSort(field, order) {
    router.push({ name: route.name, query: { ...route.query, sort_field: field, sort_order: order } });
}

const tableRef = ref(null);

/**
 * Buefy applies `default-sort` only on the table's first render (its internal
 * `firstTimeSort` guard), so a sort picked in the toolbar dropdown reordered the
 * rows but left the column headers showing the old sorted column. Re-run its
 * initSort whenever the route sort changes; raising the guard also stops the
 * table from echoing a `sort` event back into the route, and we lower it again
 * so header clicks keep emitting.
 *
 * `flush: 'post'` matters: initSort reads the table's own `default-sort` prop,
 * which is still the previous value until the parent re-renders.
 */
watch(
    [currentSortField, currentSortOrder],
    () => {
        const table = tableRef.value;
        if (!table) {
            return;
        }
        if (
            table.currentSortColumn?.field === currentSortField.value &&
            (table.isAsc ? 'asc' : 'desc') === currentSortOrder.value
        ) {
            return;
        }
        table.firstTimeSort = true;
        // A sort on a field with no matching column (extra `sortOptions`) leaves
        // initSort a no-op, so clear the indicator first instead of stranding it on
        // the previously sorted header.
        table.currentSortColumn = {};
        table.initSort();
        table.firstTimeSort = false;
    },
    { flush: 'post' }
);

// Table row click → open the preview (collapses to the split layout)
function openRow(row) {
    if (!row || row._isGroupHeader) {
        return;
    }

    // No page to go to: the record is edited where it was created.
    if (!props.previewRoute) {
        editItem(row);

        return;
    }

    router.push(getItemRoute(row));
}

const listFlexStyle = computed(() => (props.listFlex ? { flex: `${props.listFlex} 1 0%` } : {}));

// Narrow layout only: the toolbar and the row list are both absolutely
// positioned inside the panel, so anything between them in the flow — the
// `notice` strip — is painted *under* the rows. Measuring it and publishing the
// height is what lets the CSS reserve the room (see `--list-notice-h` in
// global.scss); a strip that changes size (a chart shown, a filter bar wrapping
// to two lines) republishes it.
const noticeRef = ref(null);
const noticeHeight = ref(0);
let noticeObserver = null;

watch(
    () => [noticeRef.value, tableMode.value],
    ([el]) => {
        noticeObserver?.disconnect();
        noticeObserver = null;
        if (!el || tableMode.value) {
            noticeHeight.value = 0;
            return;
        }
        noticeObserver = new ResizeObserver(() => {
            noticeHeight.value = el.offsetHeight;
        });
        noticeObserver.observe(el);
        noticeHeight.value = el.offsetHeight;
    },
    { flush: 'post' }
);

onBeforeUnmount(() => noticeObserver?.disconnect());

/*
 * The filter bar stays on one line, and gives way in steps: first « Filtrer »
 * and « Affichage » fold to their icons (each keeps its title), then the chips
 * step aside from the right, counted on a « +N » that opens « Filtrer » — which
 * holds every one. A chip holding a filter steps aside last.
 *
 * Decided on the chips' own widths, never on whether the bar fits right now:
 * folding is what makes it fit, and reading the result would undo it on the
 * next frame. A chip set aside stays laid out, unseen, so its width is known.
 */
const toolbarRef = ref(null);
const filterMenuRef = ref(null);
const compactToolbar = ref(false);
const hiddenChips = ref([]);
let toolbarLabelsWidth = 0;
let toolbarObserver = null;
let settlePasses = 0;

function openFilterMenu() {
    nextTick(() => filterMenuRef.value?.open());
}

function sameList(a, b) {
    return a.length === b.length && a.every((value, index) => value === b[index]);
}

function measureToolbar(settling = false) {
    if (!settling) {
        settlePasses = 0;
    }

    const nav = toolbarRef.value;
    const menu = nav?.querySelector('.navbar-menu');
    const bar = nav?.querySelector('.list-filter-bar');
    const barItem = nav?.querySelector('.baselist-filter-bar');
    const end = nav?.querySelector('.navbar-end');

    if (!menu || !bar || !barItem || !end || !barItem.offsetParent) {
        compactToolbar.value = false;
        hiddenChips.value = [];
        return;
    }

    // The words are measured while shown; folded, the last reading stands.
    if (!compactToolbar.value) {
        toolbarLabelsWidth = [...end.querySelectorAll('.baselist-toolbar-label')].reduce(
            (sum, label) => sum + label.offsetWidth + parseFloat(getComputedStyle(label.parentElement).columnGap || 0),
            0
        );
    }

    // Fractional widths and margins: Buefy spaces adjacent dropdowns with a margin,
    // and whole pixels rounded six times fall short of what the wrap decides on.
    const outerWidth = el => {
        const style = getComputedStyle(el);

        return el.getBoundingClientRect().width + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
    };
    const chips = [...bar.querySelectorAll(':scope > [data-chip]')];
    const more = bar.querySelector(':scope > [data-more]');
    const gap = parseFloat(getComputedStyle(bar).columnGap) || 0;
    const barStyle = getComputedStyle(barItem);
    const widthOf = shown =>
        shown.reduce((sum, chip) => sum + outerWidth(chip), 0) +
        gap * Math.max(shown.length - 1, 0) +
        parseFloat(barStyle.paddingLeft) +
        parseFloat(barStyle.paddingRight);
    // The title yields to the bar (min-width: 0), so its box may already be cut
    // short: what it needs is its content width, not what is left of it.
    const besideWidth = [...barItem.parentElement.children]
        .filter(child => child !== barItem)
        .reduce((sum, child) => sum + Math.max(outerWidth(child), child.scrollWidth), 0);
    const endWithLabels = outerWidth(end) + (compactToolbar.value ? toolbarLabelsWidth : 0);
    // A few pixels of slack: `clientWidth` is rounded, and a bar that fits to the
    // last fraction on paper wraps on screen.
    const room = menu.clientWidth - besideWidth - 4;

    if (widthOf(chips) + endWithLabels <= room) {
        compactToolbar.value = false;
        hiddenChips.value = [];
        return;
    }

    compactToolbar.value = true;
    const chipRoom = room - (endWithLabels - toolbarLabelsWidth);

    if (widthOf(chips) <= chipRoom) {
        hiddenChips.value = [];
        return;
    }

    // Set aside from the right, an empty chip at a time, until the rest and the
    // « +N » fit. Only when no empty one is left do the active ones follow: the
    // search bar above still shows every filter held, and a bar wrapped onto a
    // second line covers the table.
    const shown = [...chips];
    const hidden = [];
    for (const setAsideActive of [false, true]) {
        for (let index = chips.length - 1; index >= 0 && widthOf([...shown, more]) > chipRoom; index--) {
            if (hidden.includes(index) || Boolean(chips[index].dataset.active) !== setAsideActive) {
                continue;
            }
            shown.splice(shown.indexOf(chips[index]), 1);
            hidden.push(index);
        }
    }
    hidden.sort((a, b) => a - b);

    if (!sameList(hidden, hiddenChips.value)) {
        hiddenChips.value = hidden;
    }
}

watch(
    () => [toolbarRef.value, showFilterBar.value],
    ([nav, shown]) => {
        toolbarObserver?.disconnect();
        toolbarObserver = null;
        if (!nav || !shown) {
            compactToolbar.value = false;
            hiddenChips.value = [];
            return;
        }
        toolbarObserver = new ResizeObserver(() => measureToolbar());
        toolbarObserver.observe(nav);
        nextTick(() => {
            // A chip grows with its value (« Assigné à : Israël Morin-Tremblay »).
            nav.querySelectorAll('.list-filter-bar > [data-chip]').forEach(chip => toolbarObserver?.observe(chip));
            measureToolbar();
        });
    },
    { flush: 'post' }
);

watch(
    () => route.query,
    () => nextTick(measureToolbar)
);

// Folding the words or setting a chip aside changes nothing the observer sees
// (the toolbar keeps its width), so the settled layout is read once more. The
// decision rests on natural widths alone, so this second reading agrees — and
// should it not, two passes are all it gets before a real change asks again.
watch([compactToolbar, hiddenChips], () => {
    if (settlePasses++ < 2) {
        nextTick(() => measureToolbar(true));
    }
});

onBeforeUnmount(() => toolbarObserver?.disconnect());

const panelStyle = computed(() => ({
    ...(tableMode.value ? {} : listFlexStyle.value),
    '--list-notice-h': `${noticeHeight.value}px`
}));
const previewFlexStyle = computed(() => (props.previewFlex ? { flex: `${props.previewFlex} 1 0%` } : {}));

// When the list is configured to outweigh the preview, it is the main pane and
// must keep its width on small desktops instead of collapsing to the 275px nav.
const isListDominant = computed(() => {
    const lf = Number(props.listFlex);
    const pf = Number(props.previewFlex);
    return Number.isFinite(lf) && Number.isFinite(pf) && lf > pf;
});

// Check if a row is the currently active one
function isRowActive(rowId) {
    // Without a detail route nothing is ever "open", so no row is highlighted —
    // and the id of whatever record the surrounding page holds is not ours.
    if (!props.previewRoute) {
        return false;
    }

    // Embedded, `route.params.id` is the host record's: a row that happens to
    // share its number would light up for no reason.
    return props.embedded ? false : route.params.id == rowId;
}

/**
 * Bring the open record into view in the list.
 *
 * Arriving from the search bar or a shared link, the record is highlighted but
 * may sit far down the loaded rows — the panel then looks like it is showing
 * something else entirely. `nearest` keeps an already-visible row where it is,
 * so nothing jumps while browsing.
 *
 * Once per record: the rows watch is only there for a record whose row had not
 * loaded yet. Re-running it on every page the reader scrolls in pulled the list
 * back up to the open record each time one arrived.
 */
let revealedRowId = null;

function scrollActiveRowIntoView() {
    const id = route.params.id;
    if (!id) {
        revealedRowId = null;
        return;
    }
    if (revealedRowId === id) {
        return;
    }

    nextTick(() => {
        const row = menuListRef.value?.querySelector('.list-item-link.is-active');
        if (!row) {
            return;
        }
        revealedRowId = id;
        row.scrollIntoView({ block: 'nearest' });
    });
}

watch([() => route.params.id, () => rows.value.length], scrollActiveRowIntoView, { immediate: true });

// Get route for item (strip subquery — it belongs to sub-tabs, not preview —
// and from_name/from_id — back markers set by the opener, never inherited here)
function getItemRoute(row) {
    const { subquery: _, from_name: _fn, from_id: _fi, ...query } = route.query;

    // A list whose rows do not live at `/<resource>/<id>` says where they do:
    // a project's tasks open the project's own task page, which needs the
    // project in the route as well as the task.
    if (props.itemRoute) {
        return { query, ...props.itemRoute(row) };
    }

    return {
        name: props.previewRoute,
        params: { id: row.id },
        query
    };
}

// Get item label
function getItemLabel(row) {
    if (props.labelFn) return props.labelFn(row);
    return row[props.labelField];
}

// What the list is not finding, and the one move that follows from it.
const searchText = computed(() => (filters.query || '').trim());

const emptyLabel = computed(() => {
    if (!searchText.value) return null;
    return i18n.t('No result for « {text} »', { text: searchText.value });
});

// The offer only stands where the list can create at all, and where the module
// said what its search text means as a record.
const emptyActionLabel = computed(() => {
    if (!props.showAdd || !props.addFromSearch || !searchText.value) return null;
    return i18n.t('Add « {text} »', { text: searchText.value });
});

function addFromSearchText() {
    handleAdd(props.addFromSearch?.(searchText.value) || {});
}

// Handle add with navigation
async function handleAdd(extraFormData = {}) {
    if (props.addRoute) {
        router.push(typeof props.addRoute === 'string' ? { name: props.addRoute } : props.addRoute);
        return;
    }

    let prefill = {};

    if (props.beforeAdd) {
        try {
            prefill = (await props.beforeAdd()) || {};
        } catch {
            // Closed before choosing: nothing to add.
            return;
        }
    }

    addItem({ ...props.formData, ...prefill, ...extraFormData })
        .then(row => {
            // A dismissed form resolves with nothing — there is no record to
            // open, and reading an id off it would throw into the catch below,
            // which reads as "cancelled" while hiding a real error.
            if (!row) {
                return;
            }

            const { subquery: _, ...query } = route.query;
            emit('added', row);
            router.push(
                props.afterAdd?.(row) || {
                    name: props.previewRoute,
                    params: { id: row.id },
                    query
                }
            );
        })
        .catch(() => {
            // Modal cancelled
        });
}

// Handle edit
function handleEdit(data, modalOptions = {}) {
    return editItem(data, modalOptions)
        .then(row => {
            emit('edited', row);
            return row;
        })
        .catch(() => {
            // Modal cancelled
        });
}

// Handle remove
function handleRemove(row, options = {}) {
    return removeItem(row, options)
        .then(() => {
            emit('removed', row);
        })
        .catch(() => {
            // Modal cancelled or error
        });
}

// Drop the selection without touching a record — the way back out of selection
// mode when the checkboxes were a slip or the wrong rows got ticked.
function clearSelection() {
    checked.value = [];
}

// Handle remove selection with redirect
function handleRemoveSelection() {
    removeSelection({
        listRef: menuListRef,
        redirectRoute: props.listRoute
    });
}

// Handle reset with emit
function handleReset() {
    syncFiltersFromRoute();
    resetList();
    emit('reset');
    // Grouping needs the full dataset (see loadAllPages). When a grouping is
    // active the infinite-loader is disabled, so pull every page here instead.
    if (activeGroupBy.value) {
        loadAllPages();
    }
}

// Turning a grouping on (without a filter change, so no reset fires) still needs
// the remaining pages loaded so the groups and their counts are complete.
watch(activeGroupBy, (field, previous) => {
    if (field && !previous) {
        loadAllPages();
    }
});

// Persist filter state to localStorage so navigating back restores them
const filtersStorageKey = computed(() => `list_filters_${props.resource}_${route.params.org || 'global'}`);

// Watch for query changes
watch(
    () => route.query,
    newQuery => {
        if (!route.params.id) {
            localStorage.setItem(filtersStorageKey.value, JSON.stringify(newQuery));
        }
        if (shouldResetOnQueryChange(newQuery)) {
            handleReset();
        }
    },
    { deep: true }
);

// Watch for static params changes (e.g. route-driven type filter)
watch(
    () => JSON.stringify(props.staticParams),
    () => handleReset()
);

// --- Background freshness (opt-in via refreshOnFocus) ---
//
// A list is a snapshot taken at mount time: due dates creep closer and other
// users change rows while the tab sits in the background. Two triggers keep it
// honest — coming back to the tab, and a slow poll while it stays visible.
// Both go through the silent refreshList() so the rows swap in place.

const { now } = useNow();

// Human-readable "last synced" for the toolbar indicator. Driven by the shared
// clock so the tooltip itself doesn't go stale while the dropdown is open.
const freshnessLabel = computed(() => {
    if (refreshing.value) {
        return i18n.t('Refreshing…');
    }
    if (!lastLoadedAt.value) {
        return i18n.t('Refresh');
    }
    if (now.value - lastLoadedAt.value < 60000) {
        return i18n.t('Updated just now');
    }
    return i18n.t('Updated {time}', { time: window.moment(lastLoadedAt.value).from(now.value) });
});

// Manual click: prefer the silent path, but fall back to a full reset when
// refreshList declines (too many loaded pages, or a load already in flight).
async function forceRefresh() {
    const refreshed = await refreshList();
    if (!refreshed && !refreshing.value) {
        handleReset();
    }
}

let pollTimer = null;

function refreshIfStale() {
    if (document.visibilityState !== 'visible') return;
    if (!lastLoadedAt.value) return;
    if (Date.now() - lastLoadedAt.value < props.staleAfter) return;
    refreshList();
}

function onPollTick() {
    if (document.visibilityState !== 'visible') return;
    refreshList();
}

function startBackgroundRefresh() {
    // `focus` and `visibilitychange` both fire on a single alt-tab; the
    // staleAfter guard inside refreshIfStale collapses them into one request.
    document.addEventListener('visibilitychange', refreshIfStale);
    window.addEventListener('focus', refreshIfStale);

    if (props.pollInterval > 0) {
        pollTimer = setInterval(onPollTick, props.pollInterval);
    }
}

function stopBackgroundRefresh() {
    document.removeEventListener('visibilitychange', refreshIfStale);
    window.removeEventListener('focus', refreshIfStale);
    if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
    }
}

onBeforeUnmount(stopBackgroundRefresh);

// Lifecycle
onMounted(() => {
    if (props.title) {
        document.title = `${props.title} | ${AppTitle}`;
    }
    handleReset();

    // The user's per-list preferences decide the layout itself, so they are needed
    // before a record is opened — not only once a resizable grid is on screen. One
    // batched, cached request for every grid; concurrent callers share it.
    if (props.objectType) {
        gridPreferences.loadAll();
    }

    if (props.refreshOnFocus) {
        startBackgroundRefresh();
    }
});

// What the search bar needs to talk about this list from the navbar: its search
// declaration, its API resource, and the parameters it always sends.
watch(
    [() => props.resource, () => props.objectType, () => props.staticParams],
    () =>
        setActiveList({
            resource: props.resource,
            objectType: props.objectType,
            staticParams: { ...props.staticParams }
        }),
    { immediate: true, deep: true }
);

onBeforeUnmount(() => clearActiveList(props.resource));

// Wrapper for infinite load
function onInfiniteLoad($state) {
    baseOnInfiniteLoad($state);
}

// Expose for parent components if needed
defineExpose({
    rows,
    checked,
    filters,
    lastLoadedAt,
    refreshing,
    resetList: handleReset,
    reloadList: handleReset,
    // Silent in-place refresh (keeps scroll + selection). Used by realtime
    // signals; falls back to a full reset via forceRefresh when it declines.
    refreshList,
    forceRefresh,
    addItem: handleAdd,
    editItem: handleEdit,
    removeItem: handleRemove
});
</script>

<style scoped>
.content-list {
    padding-bottom: 1em;
}
.list-table :deep(tbody tr) {
    cursor: pointer;
}
/* A grid wider than the screen scrolls inside its own frame — it never pushes
   the page.
   Resizing a column is *meant* to widen the table (see `pinAllColumns` in
   `directives/resizableCols.js`, which pins every column so the dragged one
   grows instead of stealing from its neighbour), but nothing ever gave that
   width somewhere to go: Buefy only makes `.table-wrapper` scroll for a
   `scrollable` table. So a saved layout from a wide monitor, or one column
   dragged long, dragged the whole page sideways with it — toolbar, header and
   all.
   Every picker a cell opens is appended to the body, so none of them is
   clipped by this box. */
/* The grid's horizontal scrollbar shows as soon as the pointer is over the
   table, wherever the rows are scrolled to. Two things hid it. The rows scrolled in `.content-list`, so the bar sat at
   the foot of the whole table, below the fold on any list longer than the
   screen: the wrapper now fills the panel and scrolls both ways itself, which
   pins its bar to the bottom of the visible frame (the infinite loader
   resolves its scroll root on every re-arm, so it follows). And the app-wide
   `scrollbar-width` / `scrollbar-color` make Chrome draw the system's overlay
   bar, which macOS hides until the pointer finds it; resetting both to `auto`
   hands the bar back to the app's thin `::-webkit-scrollbar` rules
   (global.scss), revealed on hover — the same bar as the vertical one.
   Because the wrapper is now the vertical scroller, the column headers stay
   pinned to its top while the rows scroll under them. */
.content-list.list-table {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
}
.list-table :deep(.b-table) {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
}
.list-table :deep(.table-wrapper) {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    scrollbar-width: auto;
    scrollbar-color: auto;
}
/* Alt held over the grid offers to drag it (`v-drag-scroll`); dragging grips it. */
.list-table :deep(.table-wrapper.is-drag-ready),
.list-table :deep(.table-wrapper.is-drag-ready *) {
    cursor: grab;
}
.list-table :deep(.table-wrapper.is-drag-scrolling),
.list-table :deep(.table-wrapper.is-drag-scrolling *) {
    cursor: grabbing;
}
.list-table :deep(thead th) {
    position: sticky;
    top: 0;
    z-index: 2;
    background: var(--bulma-scheme-main);
}
/* Toolbar buttons: keep the icon and its optional desktop label on one line */
.baselist-toolbar-btn {
    gap: 0.35rem;
}
/* The filter bar needs the room: the toolbar's words yield to it (measureToolbar). */
.navbar.is-compact .baselist-toolbar-label {
    display: none;
}
/* Narrow list panel (split list + preview): the toolbar icons and the Add
   button share a cramped header row, so trim their horizontal padding well
   below Bulma's default 0.75rem to give the Add button room. Full table mode
   has ample width and keeps the default spacing. */
.list-panel:not(.list-panel--table) :deep(.navbar-end .navbar-item) {
    padding-left: 0.55rem;
    padding-right: 0.55rem;
}
/* The rail is a fixed 275px with `overflow-x: hidden`, so anything the toolbar
   cannot fit is not wrapped or scrolled — it is silently cut off at the edge.
   Nothing in the bar used to yield: the left cluster kept its natural width and
   pushed the icons out of the panel. So the left side is allowed to shrink and
   truncate, and the buttons are not: a clipped icon is unreachable, a shortened
   title still reads. Applies to both bars — the normal one and the selection
   one, which shares the same rail. */
/* The whole chain has to yield, not just the outer box. Two Bulma defaults stand
   in the way: `.navbar-menu` is `flex-shrink: 0` and so is every `.navbar-item`,
   so the left cluster keeps its content width whatever the panel is worth. And a
   module's #filters slot is usually a dropdown, which adds .dropdown and
   .dropdown-trigger between the bar and the label — a single min-width on
   .navbar-start leaves those at their content width too. No `overflow: hidden`
   anywhere on this side: those dropdowns are not appended to the body, and
   hiding the overflow would clip their open menu. */
.list-panel :deep(.navbar-menu),
.list-panel :deep(.navbar-start),
.list-panel :deep(.navbar-start > *),
.list-panel :deep(.navbar-start .dropdown),
.list-panel :deep(.navbar-start .dropdown-trigger),
.list-panel :deep(.navbar-start .navbar-item) {
    min-width: 0;
}
.list-panel :deep(.navbar-menu),
.list-panel :deep(.navbar-start),
.list-panel :deep(.navbar-start > *),
.list-panel :deep(.navbar-start .navbar-item) {
    flex-shrink: 1;
}
.list-panel :deep(.navbar-end) {
    flex-shrink: 0;
}
/* The filter bar yields before the title does, and wraps its chips onto a second
   line rather than pushing the « Filtrer » button out of the toolbar. */
/* With a filter bar, the left side takes the free width of the toolbar and the
   bar centres its chips in it — between the title and the buttons, wrapping onto
   a second line rather than pushing « Filtrer » out of the toolbar. */
.list-panel :deep(.navbar-start.has-filter-bar) {
    flex-grow: 1;
}
.baselist-filter-bar {
    flex: 1 1 auto;
    justify-content: center;
    padding-top: 0.35rem;
    padding-bottom: 0.35rem;
}
.baselist-filter-bar :deep(.list-filter-bar) {
    flex: 1 1 auto;
    justify-content: center;
}
/* Freshness indicator: deliberately low-contrast — it's an ambient status cue,
   not an action competing with the filter/sort/add controls. */
.baselist-freshness {
    color: var(--bulma-text-weak);
}
.baselist-freshness:hover {
    color: var(--bulma-text);
}
.baselist-freshness :deep(.is-spinning) {
    animation: baselist-spin 1s linear infinite;
}
@keyframes baselist-spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
/* Primary "Add" action stands out from the icon-only toolbar buttons */
.baselist-add-item {
    padding-top: 0.35rem;
    padding-bottom: 0.35rem;
}
/* Pinned, not merely last: the panel it sits in is what scrolls, so a footer
   left as an ordinary flex child slides away under the rows as soon as the
   table is longer than the screen — which is most of the time, and is how a
   quick-add bar goes missing. */
.list-table-footer {
    flex: 0 0 auto;
    position: sticky;
    bottom: 0;
    z-index: 6;
    background: var(--bulma-scheme-main);
    border-top: 1px solid var(--bulma-border-weak);
    border-bottom-left-radius: var(--bulma-radius-large);
    border-bottom-right-radius: var(--bulma-radius-large);
}
.group-header-item {
    list-style: none;
    pointer-events: none;
}
.group-header {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem 0.25rem;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    border-bottom: 1px solid var(--bulma-border-weak);
}
/* Table-mode group header rows (same look as the list-panel headers). The
   label lives in the first data cell and overflows across the empty ones. */
.list-table :deep(tr.is-group-header-row td) {
    background: var(--bulma-background) !important;
    white-space: nowrap;
    overflow: visible;
    cursor: default;
}
.table-group-header {
    display: inline-flex;
    align-items: center;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}
/* List-panel footer: hidden on desktop (the table-mode footer is used there),
   shown below the desktop breakpoint via mobile.scss / tablet.scss. */
/* Thin advisory strip between the toolbar and the rows (see the `notice` slot). */
.list-notice:empty {
    display: none;
}
.list-notice {
    flex: 0 0 auto;
    padding: 0.4rem 0.75rem;
    font-size: 0.85rem;
    background: var(--bulma-scheme-main-bis);
    border-bottom: 1px solid var(--bulma-border-weak);
    color: var(--bulma-text-weak);
}
.list-footer {
    display: none;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--bulma-scheme-main);
    border-top: 1px solid var(--bulma-border-weak);
    border-bottom-left-radius: var(--bulma-radius-large);
    border-bottom-right-radius: var(--bulma-radius-large);
    z-index: 25;
}
</style>
